import { useEffect, useState } from 'react';
import { inspectionService } from '../../../services/inspectionService';
import { track } from '../../../services/telemetry';
import {
  SEND_POLL_MS,
  secondsUntil,
  sentMessage,
  type SendReceipt,
} from '../sendHold';

type SendResult = {
  success: boolean;
  message: string;
  recipients?: Array<{ email: string; name: string; status: string }>;
  /** JIM-0922-01: who was asked to sign the report (or auto-signed). */
  clientSignature?: {
    status: string;
    signer_name: string;
    signer_email: string;
    auto_applied?: boolean;
  } | null;
} | null;

type OwnedSendResult =
  | (NonNullable<SendResult> & { inspectionId: string })
  | null;

/** A report waiting out its hold: when it will be emailed. */
export type SendHold = { sendsAt: string };
type OwnedSendHold = (SendHold & { inspectionId: string }) | null;

function resultFromReceipt(receipt: SendReceipt): NonNullable<SendResult> {
  return {
    success: true,
    message: sentMessage(receipt),
    recipients: receipt.recipients,
    clientSignature: receipt.client_signature ?? null,
  };
}

/**
 * Report + send state for the inspection identified by `submittedInspectionId`.
 *
 * Every piece of state here belongs to exactly one inspection. When the id
 * changes (Edit → Review Report again, or a new inspection on the same mounted
 * page) the previous inspection's report URL, error and send result are
 * dropped, and a send result is only ever surfaced for the inspection it was
 * produced for. Jim 2026-09-04: a report showed "Sent to Client" although it
 * had never been sent — the screen was still showing the previous
 * inspection's result.
 *
 * Send to Client does not email at once (JIM-1003-05): the report is queued
 * and the server sends it ten minutes later. `sendHold` is that wait; Edit
 * calls `stopSend` first, and once the time has come the hook asks the server
 * how the send went and turns the hold into the usual send result.
 */
export function useReportGeneration(
  submittedInspectionId: string | null,
  addDebug: (msg: string) => void
) {
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [ownedSendResult, setOwnedSendResult] = useState<OwnedSendResult>(null);
  const [ownedSendHold, setOwnedSendHold] = useState<OwnedSendHold>(null);
  const [stateOwner, setStateOwner] = useState<string | null>(
    submittedInspectionId
  );

  useEffect(() => {
    if (submittedInspectionId === stateOwner) return;
    setStateOwner(submittedInspectionId);
    setReportUrl(null);
    setReportError(null);
    setOwnedSendResult(null);
    setOwnedSendHold(null);
    setIsSendingReport(false);
  }, [submittedInspectionId, stateOwner]);

  const sendHold: SendHold | null =
    ownedSendHold && ownedSendHold.inspectionId === submittedInspectionId
      ? { sendsAt: ownedSendHold.sendsAt }
      : null;

  // Once the hold's time has come, ask the server how the send went until it
  // says sent, failed, or that the clock was stopped somewhere else.
  const heldId = sendHold ? submittedInspectionId : null;
  const heldUntil = sendHold?.sendsAt ?? null;
  useEffect(() => {
    if (!heldId || !heldUntil) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      try {
        const status = await inspectionService.getSendStatus(heldId);
        if (cancelled) return;
        if (status.state === 'sent') {
          setOwnedSendHold(null);
          setOwnedSendResult({
            inspectionId: heldId,
            ...resultFromReceipt(
              status.result ?? {
                sent_count: 0,
                recipients: [],
                no_contacts: false,
              }
            ),
          });
          return;
        }
        if (status.state === 'failed' || status.state === 'idle') {
          setOwnedSendHold(null);
          if (status.state === 'failed')
            setReportError(
              `Send to client failed: ${status.error ?? 'unknown error'}. Try again, or send it later from Unsent Reports on your dashboard.`
            );
          return;
        }
      } catch {
        // Offline or a blip: keep asking.
      }
      if (!cancelled) timer = setTimeout(check, SEND_POLL_MS);
    };
    timer = setTimeout(
      check,
      secondsUntil(heldUntil, Date.now()) * 1000 + 1500
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [heldId, heldUntil]);

  const sendResult: SendResult =
    ownedSendResult && ownedSendResult.inspectionId === submittedInspectionId
      ? ownedSendResult
      : null;

  const setSendResult = (result: SendResult) => {
    if (!result || !submittedInspectionId) {
      setOwnedSendResult(null);
      return;
    }
    setOwnedSendResult({ ...result, inspectionId: submittedInspectionId });
  };

  const generateReport = async () => {
    if (!submittedInspectionId) return;

    setIsGeneratingReport(true);
    setReportError(null);
    addDebug(`Generating report for inspection: ${submittedInspectionId}`);

    try {
      const data = await inspectionService.generateReport(
        submittedInspectionId
      );

      addDebug(`Report generated: ${data.report_url}`);
      // Usage telemetry: manual retry path (the auto-generate path after
      // submit tracks report_generated in LiteInspectionFlowPage).
      track('report_generated', {
        inspection_id: submittedInspectionId,
        retry: true,
      });
      setReportUrl(data.report_url);
    } catch (err: unknown) {
      console.error('[ReportGen] Failed to generate report:', err);
      setReportError(
        err instanceof Error ? err.message : 'Failed to generate report'
      );
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const approveAndSend = async () => {
    if (!submittedInspectionId) {
      console.error(
        '[ApproveAndSend] No submittedInspectionId — submission failed or ID was never set'
      );
      setReportError(
        'Unable to send: this inspection never finished completing on the server. Go back and complete it again.'
      );
      return;
    }
    const inspectionId = submittedInspectionId;

    setIsSendingReport(true);
    setReportError(null);
    addDebug(`Queue send for inspection: ${inspectionId}`);

    try {
      const data = await inspectionService.queueSend(inspectionId);

      if (data.queued) {
        if (data.sends_at)
          setOwnedSendHold({ inspectionId, sendsAt: data.sends_at });
        return;
      }
      // The hold is switched off on this server: the report went at once.
      setReportUrl(data.report_url);
      setOwnedSendResult({
        inspectionId,
        ...resultFromReceipt({
          sent_count: data.email_results.sent_count,
          recipients: data.email_results.recipients,
          no_contacts: data.no_contacts,
          client_signature: data.client_signature ?? null,
        }),
        success: data.success,
      });
    } catch (err) {
      console.error('[ApproveAndSend] Failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send report';
      setReportError(
        `Send to client failed: ${errorMessage}. Nothing was emailed — try again, or send it later from Unsent Reports on your dashboard.`
      );
    } finally {
      setIsSendingReport(false);
    }
  };

  /**
   * Stop the clock (Edit was tapped). False when the send has already
   * started and can no longer be stopped — the hold stays and resolves to
   * the send result.
   */
  const stopSend = async (): Promise<boolean> => {
    if (!submittedInspectionId || !sendHold) return true;
    try {
      await inspectionService.cancelSend(submittedInspectionId);
      setOwnedSendHold(null);
      return true;
    } catch {
      return false;
    }
  };

  return {
    isGeneratingReport,
    setIsGeneratingReport,
    reportUrl,
    setReportUrl,
    reportError,
    setReportError,
    isSendingReport,
    setIsSendingReport,
    sendResult,
    setSendResult,
    sendHold,
    stopSend,
    generateReport,
    approveAndSend,
  };
}
